import { Injectable } from '@angular/core';
import {
  ComponentsService,
  PlaylistComponentInfo,
  PlaylistComponentItem,
} from '../../services/components.service';
import { AttributeDataService } from '../../services/attribute-data.service';
import { MediaSelectorService } from './media-selector.service';
import { TemplateEditorService } from '../../services/template-editor.service';
import { CurrentPlanService } from 'src/app/components/plans/current-plan.service';
import { AnalyticsFactory } from 'src/app/ajs-upgraded-providers';
import { StorageManagerService } from 'src/app/storage/services/storage-manager.service';
import { environment } from 'src/environments/environment';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { WebPageService } from './web-page.service';

export type BasePlaylistTemplateItem = {
  id?: string;
  label?: string;
  tagName: string;
  attributes?: { [key: string]: string | number | boolean | object };
};

@Injectable({
  providedIn: 'root',
})
export class BasePlaylistService {
  public static readonly FOLDER_TYPE = {
    GOOGLE_PHOTOS_ALBUM: 'google-photos-album',
    CANVA: 'canva',
  };

  playlistComponents: PlaylistComponentItem[] =
    ComponentsService.PLAYLIST_COMPONENTS;
  playlistItems: any[] = [];
  componentId: string;

  constructor(
    protected componentsService: ComponentsService,
    protected mediaSelectorService: MediaSelectorService,
    protected templateEditorService: TemplateEditorService,
    protected webPageService: WebPageService,
    protected currentPlanService: CurrentPlanService,
    protected attributeDataService: AttributeDataService,
    protected analyticsFactory: AnalyticsFactory,
    protected storageManagerService: StorageManagerService,
    protected companyStateService: CompanyStateService,
  ) {}

  protected getComponentObject(
    item: BasePlaylistTemplateItem,
  ): PlaylistComponentInfo {
    return {
      type: item.tagName,
      label: item.label,
      id: this.componentId + ' ' + this.playlistItems.indexOf(item),
      layoutId: (item.attributes?.layout as any)?.layoutId,
    };
  }

  protected safeParseJson(string: string): any {
    try {
      return JSON.parse(string);
    } catch (err) {
      console.error(err, string);
      return null;
    }
  }

  protected getGooglePhotosItemAttributes() {
    return {
      type: BasePlaylistService.FOLDER_TYPE.GOOGLE_PHOTOS_ALBUM,
      environment: environment.production ? 'prod' : 'test',
    };
  }

  protected getCanvaItemAttributes() {
    return {
      type: BasePlaylistService.FOLDER_TYPE.CANVA,
      environment: environment.production ? 'prod' : 'test',
    };
  }

  addItem(type: string, options?: any): void {}

  getComponentName(item: BasePlaylistTemplateItem) {
    return this.componentsService.getComponentName(
      this.getComponentObject(item),
    );
  }

  getComponentIcon(item: BasePlaylistTemplateItem) {
    return this.componentsService.getComponentIcon(
      this.getComponentObject(item),
    );
  }

  isMatIcon(item: BasePlaylistTemplateItem) {
    return this.componentsService.isMatIcon(this.getComponentObject(item));
  }

  getComponentByType(type: string): PlaylistComponentItem {
    return (
      ComponentsService.COMPONENTS_ARRAY.find((item) => {
        return item.type === type;
      }) || ({} as PlaylistComponentItem)
    );
  }

  getComponentByTypeOrLayoutId(
    type: string,
    layoutId: string,
  ): PlaylistComponentItem {
    let componentType = type;

    if (type === 'rise-layout' && layoutId) {
      componentType = ComponentsService.LAYOUT_DIRECTIVES_MAP[layoutId];
    }

    return this.getComponentByType(componentType);
  }

  editComponent(
    item: BasePlaylistTemplateItem,
    playlist: boolean = false,
    options?: any,
  ) {
    this.componentsService.editComponent(this.getComponentObject(item), {
      playlist,
      ...options,
    });
  }

  reset() {
    this.componentId = '';
    this.playlistItems = [];
    this.playlistComponents = ComponentsService.PLAYLIST_COMPONENTS;
  }

  showMediaSelector(options?: any) {
    this.mediaSelectorService.onMediaUpdate = null;
    this.mediaSelectorService.onUserComponentAdd = null;

    const fileType = options && 'fileType' in options ? options.fileType : null;
    const singleFile =
      options && 'singleFile' in options ? options.singleFile : false;
    const stockLibrary =
      options && 'stockLibrary' in options ? options.stockLibrary : false;

    this.componentsService.editComponent(
      {
        type: 'rise-media-selector',
      },
      {
        fileType,
        singleFile,
        stockLibrary,
      },
    );
  }
}
